import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { confirmGuestOrder, formatUsd } from '@/lib/api';
import { useCart } from '@/lib/cartStore';
import styles from './CheckoutSuccessPage.module.css';

/** Post-Stripe return. Confirms the session actually PAID (converging on the
 *  same idempotent order creator the webhook uses), shows the order number, and
 *  clears the cart. The confirmation email goes to the guest_email Stripe
 *  collected. */
export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const clear = useCart((s) => s.clear);
  const [state, setState] = useState<'loading' | 'paid' | 'pending' | 'error'>('loading');
  const [order, setOrder] = useState<{ order_number?: string; total_cents?: number }>({});
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let active = true;
    confirmGuestOrder(sessionId)
      .then((d) => {
        if (!active) return;
        if (d.paid) {
          setState('paid');
          setOrder({ order_number: d.order_number, total_cents: d.total_cents });
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
        } else {
          setState('pending');
        }
      })
      .catch(() => {
        if (active) setState('error');
      });
    return () => {
      active = false;
    };
  }, [sessionId, clear]);

  return (
    <div className={styles.page}>
      {state === 'loading' && <p className={styles.muted}>Confirming your order…</p>}

      {state === 'paid' && (
        <>
          <span className={styles.icon}>
            <CheckCircle2 size={44} />
          </span>
          <h1 className={styles.title}>Order confirmed</h1>
          {order.order_number && <p className={styles.order}>Order {order.order_number}</p>}
          {order.total_cents != null && (
            <p className={styles.total}>{formatUsd(order.total_cents)}</p>
          )}
          <p className={styles.muted}>
            A confirmation email is on its way. We&rsquo;ll email tracking when your order ships.
          </p>
          <Link to="/" className="eviBtn eviBtnGhost">
            Continue shopping
          </Link>
        </>
      )}

      {state === 'pending' && (
        <>
          <h1 className={styles.title}>Payment processing</h1>
          <p className={styles.muted}>
            Your payment is still being confirmed. Refresh in a moment — your confirmation email
            will arrive once it clears.
          </p>
          <Link to="/" className="eviBtn eviBtnGhost">
            Back to store
          </Link>
        </>
      )}

      {state === 'error' && (
        <>
          <h1 className={styles.title}>We couldn&rsquo;t confirm this order</h1>
          <p className={styles.muted}>
            If you completed payment, your confirmation email is the record of your order. Contact
            us if anything looks wrong.
          </p>
          <Link to="/" className="eviBtn eviBtnGhost">
            Back to store
          </Link>
        </>
      )}
    </div>
  );
}
